import { useState } from 'react';
import { Card, CardTitle, CardValue } from '@/components/ui/Card';
import { DebtCard } from '@/components/debts/DebtCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { MonthSwitcher } from '@/components/ui/MonthSwitcher';
import { currentYearMonth } from '@/lib/date';
import { formatCOP } from '@/lib/format';
import { useDashboardSummary } from '@/hooks/useDashboard';
import { useDebts } from '@/hooks/useDebts';

export function DashboardPage() {
  const [month, setMonth] = useState(currentYearMonth);
  const { data: summary } = useDashboardSummary(month);
  const { data: debts } = useDebts('ACTIVE');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Resumen del mes</h1>
          <p className="text-sm text-muted-foreground">Solo tus datos — nadie más los ve.</p>
        </div>
        <MonthSwitcher value={month} onChange={setMonth} />
      </div>

      <Card>
        <CardTitle>Saldo disponible del mes</CardTitle>
        <CardValue className={(summary?.netCashflow ?? 0) < 0 ? 'text-debt' : 'text-income'}>
          {formatCOP(summary?.netCashflow)}
        </CardValue>
        <p className="mt-1 text-xs text-muted-foreground">
          Ingresos − gastos en efectivo/débito/transferencia − abonos a deudas. Las compras a crédito no restan aquí
          todavía — restan cuando pagas la tarjeta.
        </p>
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardTitle>Ingresos</CardTitle>
          <CardValue className="text-income">{formatCOP(summary?.totalIncome)}</CardValue>
        </Card>
        <Card>
          <CardTitle>Gastos</CardTitle>
          <CardValue>{formatCOP(summary?.totalExpense)}</CardValue>
        </Card>
        <Card>
          <CardTitle>Abonos a deudas</CardTitle>
          <CardValue>{formatCOP(summary?.totalDebtPayments)}</CardValue>
        </Card>
        <Card>
          <CardTitle>Saldo total en deudas</CardTitle>
          <CardValue className="text-debt">{formatCOP(summary?.totalDebtBalance)}</CardValue>
        </Card>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-medium text-muted-foreground">Deudas activas</h2>
        {debts && debts.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {debts.map((debt) => (
              <DebtCard key={debt.id} debt={debt} />
            ))}
          </div>
        ) : (
          <EmptyState title="No tienes deudas activas" description="Registra una deuda para empezar a proyectar cuándo quedas libre." />
        )}
      </div>
    </div>
  );
}
